<template>
  <section>
    <h4 class="text-h4 mb-4">
      {{ t("shipment.packingLists") }}
    </h4>

    <VCard>
      <VCardText>
        <div class="d-flex align-center flex-wrap gap-2 mb-4">
          <VBtn
            v-if="canCreatePackingList"
            color="secondary"
            prepend-icon="tabler-list-check"
            :to="{ name: 'shipment-packing-lists-create' }"
          >
            {{ t("actions.create") }}
          </VBtn>

          <VBtn
            v-if="canCreatePackingList"
            color="success"
            prepend-icon="tabler-file-upload"
            class="ms-2"
            @click="importDialog = true"
          >
            {{ t("shipment.import_excel") || "Import Excel" }}
          </VBtn>

          <VSpacer />

          <VTextField
            v-model="containerFilter"
            :label="t('shipment.filter_by_container') || 'Filtrer par conteneur'"
            placeholder="ex: 9.2 10.9"
            prepend-inner-icon="tabler-container"
            density="comfortable"
            clearable
            hide-details
            style="max-width: 260px"
            @update:model-value="onContainerFilterChange"
            @click:clear="onContainerFilterChange('')"
          />
        </div>
      </VCardText>

      <!-- Data Table -->
      <VDataTableServer
        v-model="selectedRows"
        v-model:sort-by="tableSort"
        :headers="headers"
        :items="packingLists"
        :loading="loading"
        :items-length="totalItems"
        item-value="id"
        return-object
        show-select
        @update:options="handleTableOptionsUpdate"
      >
        <!-- Custom rendering for ID column -->
        <template #item.id="{ item }">
          <div class="font-weight-medium">#{{ item.id }}</div>
        </template>

        <!-- Custom rendering for date column -->
        <template #item.created_at="{ item }">
          <span>{{ formatDate(item.created_at) }}</span>
        </template>

        <!-- Custom rendering for container column -->
        <template #item.container_arrival="{ item }">
          <div v-if="item.container_arrival">
            <VChip color="primary" size="small" label>
              {{
                item.container_arrival.reference_code ||
                item.reference_code ||
                `#${item.container_arrival.id}`
              }}
            </VChip>
            <div class="text-caption mt-1">
              {{
                item.container_arrival.departure_date
                  ? formatDate(item.container_arrival.departure_date)
                  : "No departure date"
              }}
            </div>
          </div>
          <span v-else>-</span>
        </template>

        <!-- Custom rendering for package count column -->
        <template #item.total_package="{ item }">
          <VChip color="info" size="small">
            {{ item.total_package || 0 }}
          </VChip>
        </template>

        <!-- Custom rendering for weight column -->
        <template #item.weight="{ item }">
          {{ item.weight ? `${item.weight} kg` : "-" }}
        </template>

        <!-- Custom rendering for volume column -->
        <template #item.volume="{ item }">
          {{ item.volume ? `${parseFloat(item.volume).toFixed(2)} m³` : "-" }}
        </template>

        <!-- Custom rendering for status column -->
        <template #item.status="{ item }">
          <VChip
            :color="item.status_color || getStatusColor(item.status)"
            size="small"
          >
            <VIcon
              v-if="item.status_icon"
              :icon="item.status_icon.replace('fas fa-', 'tabler-')"
              size="x-small"
              class="me-1"
            />
            {{
              t(
                `shipment.statuses.${item.status_label || item.status}`,
                item.status_label
              )
            }}
          </VChip>
        </template>

        <!-- Actions column with multiple buttons -->
        <template #item.actions="{ item }">
          <div class="d-flex gap-1">
            <VBtn
              icon
              variant="text"
              color="primary"
              size="small"
              :to="`/shipment/packing-lists/${item.id}/view`"
            >
              <VIcon>tabler-eye</VIcon>
            </VBtn>
            <VBtn
              v-if="canEditPackingList"
              icon
              variant="text"
              color="secondary"
              size="small"
              :to="`/shipment/packing-lists/${item.id}/edit`"
            >
              <VIcon>tabler-edit</VIcon>
            </VBtn>
            <VBtn
              v-if="canDeletePackingList"
              v-tooltip="!isSuperadmin ? t('shipment.superadmin_only') : ''"
              icon
              variant="text"
              color="error"
              size="small"
              :disabled="!isSuperadmin"
              @click="confirmDelete(item)"
            >
              <VIcon>tabler-trash</VIcon>
            </VBtn>
          </div>
        </template>
      </VDataTableServer>

      <!-- Batch Actions -->
      <VCardText v-if="selectedRows.length > 0">
        <div class="d-flex justify-space-between align-center">
          <div class="d-flex gap-2">
            <VBtn
              v-if="canDeletePackingList"
              v-tooltip="!isSuperadmin ? t('shipment.superadmin_only') : ''"
              color="error"
              prepend-icon="tabler-trash"
              size="small"
              :disabled="!isSuperadmin"
              @click="confirmMassDelete"
            >
              {{ t("actions.deleteSelected") }}
            </VBtn>
          </div>

          <div>
            {{ t("shipment.selectedItems", { count: selectedRows.length }) }}
            <span
              v-if="selectedRows.length > 0"
              class="text-caption text-grey ms-2"
            >
              ({{ selectedRows.map((row) => `#${row.id}`).join(", ") }})
            </span>
          </div>
        </div>
      </VCardText>
    </VCard>

    <!-- Import Excel Dialog -->
    <VDialog v-model="importDialog" max-width="600px" persistent>
      <VCard>
        <VCardTitle>
          {{ t("shipment.import_packing_list") || "Import Packing List" }}
        </VCardTitle>

        <VCardText>
          <VAlert
            v-if="importErrorMessage"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ importErrorMessage }}
          </VAlert>

          <VAlert
            v-if="importSuccessMessage"
            type="success"
            variant="tonal"
            class="mb-4"
          >
            {{ importSuccessMessage }}
          </VAlert>

          <VProgressLinear
            v-if="importLoading"
            class="mb-4"
            color="primary"
            :model-value="importProgress"
            height="20"
            rounded
            striped
          >
            <template #default>
              {{ importProgressMessage || `Processing (${importProgress}%)` }}
            </template>
          </VProgressLinear>

          <VForm @submit.prevent="uploadExcelFile">
            <VRow>
              <VCol cols="12">
                <VFileInput
                  v-model="importForm.excel_file"
                  :label="t('shipment.excel_file') || 'Excel File'"
                  accept=".xlsx,.xls"
                  :disabled="importLoading"
                  :rules="[(v) => !!v || 'Excel file is required']"
                  :placeholder="
                    t('shipment.excel_placeholder') ||
                    'Click to select or drop Excel file'
                  "
                  :hint="
                    t('shipment.excel_filename_hint') ||
                    'Filename must follow M.D_SEA (e.g. 1.23_SEA.xlsx). The container is created automatically.'
                  "
                  persistent-hint
                  @update:model-value="onFileSelected"
                />
              </VCol>

              <VCol v-if="filenamePreview" cols="12">
                <VAlert
                  :type="filenamePreview.ok ? (filenamePreview.container?.will_reuse ? 'info' : 'success') : 'error'"
                  variant="tonal"
                  density="comfortable"
                >
                  <template v-if="filenamePreview.ok">
                    <div class="font-weight-medium">
                      {{ filenamePreview.container?.will_reuse
                        ? `Conteneur existant : ${filenamePreview.parsed.reference_code}`
                        : `Nouveau conteneur : ${filenamePreview.parsed.reference_code}` }}
                    </div>
                    <div class="text-caption">
                      Départ {{ filenamePreview.parsed.departure_date }} · Mer
                      <span v-if="filenamePreview.container?.will_reuse">
                        · Le packing list sera ajouté à ce conteneur.
                      </span>
                      <span v-else>
                        · Le conteneur sera créé automatiquement à l'import.
                      </span>
                    </div>
                  </template>
                  <template v-else>
                    {{ filenamePreview.message }}
                  </template>
                </VAlert>
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="importForm.import_date"
                  :label="t('shipment.import_date') || 'Import Date'"
                  type="date"
                  :disabled="importLoading"
                  :hint="t('shipment.import_date_hint') || 'Optional — used to timestamp parcel rows.'"
                  persistent-hint
                />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            color="secondary"
            variant="outlined"
            :disabled="importLoading"
            @click="importDialog = false"
          >
            {{ t("actions.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="importLoading"
            :disabled="importLoading"
            @click="uploadExcelFile"
          >
            {{ t("actions.upload") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Delete Confirmation Dialog -->
    <VDialog v-model="deleteDialog" max-width="500px">
      <VCard>
        <VCardTitle class="text-h5">
          {{ t("messages.confirmDeleteTitle") }}
        </VCardTitle>

        <VCardText>
          {{ t("messages.confirmDelete") }}
          <strong>{{ itemToDelete?.id ? `#${itemToDelete.id}` : "" }}</strong>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn color="secondary" variant="text" @click="deleteDialog = false">
            {{ t("actions.cancel") }}
          </VBtn>
          <VBtn color="error" variant="text" @click="deleteItem">
            {{ t("actions.delete") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Mass Delete Confirmation Dialog -->
    <VDialog v-model="showConfirmDeleteDialog" max-width="500">
      <VCard>
        <VCardTitle class="text-h5">
          {{ t("messages.confirmDeleteTitle") }}
        </VCardTitle>
        <VCardText>
          {{
            t("messages.confirmDeleteMultiple", { count: selectedRows.length })
          }}
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="secondary"
            variant="text"
            @click="showConfirmDeleteDialog = false"
          >
            {{ t("actions.cancel") }}
          </VBtn>
          <VBtn color="error" @click="handleDeleteSelected">
            {{ t("actions.delete") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<script setup>
definePage({
  meta: {
    requiresAuth: true,
    action: "read",
    subject: "PackingLists", // Changed from 'Parcels' to correct subject
  },
});

import { ref, onMounted, computed, watch, nextTick } from "vue";
import { usePackingListsManagement } from "@/composables/usePackingListsManagement";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useSnackbar } from "@/composables/useSnackbar";
import { useSuperadmin } from "@/composables/useSuperadmin";
import axios from "axios";
import { useAbility } from "@/plugins/casl/composables/useAbility";
import { getStatusColor } from "@/utils/statusUtils";

const { t } = useI18n();
const router = useRouter();
const { success, error, warning } = useSnackbar();
const { isSuperadmin } = useSuperadmin();

const { can } = useAbility();

const canEditPackingList = computed(() => can("edit", "PackingLists"));
// Delete is hard-locked to superadmin (RestrictDeletesToSuperadmin
// middleware refuses the request anyway). Hide it from admins so the
// button doesn't tease.
const canDeletePackingList = computed(() => isSuperadmin.value);
const canReadPackingList = computed(() => can("read", "PackingLists"));
const canCreatePackingList = computed(() => can("create", "PackingLists"));

// Import Excel dialog state
const importDialog = ref(false);

const importForm = ref({
  excel_file: null,
  import_date: null,
});

// Preview of the parsed filename: container that will be created or reused.
// Populated by onFileSelected; null when no file selected (or invalid name).
const filenamePreview = ref(null);

const onFileSelected = async (file) => {
  filenamePreview.value = null;
  if (!file) return;
  // VFileInput can pass either a File or a single-element array depending
  // on Vuetify version — normalize.
  const f = Array.isArray(file) ? file[0] : file;
  if (!f?.name) return;
  try {
    const fd = new FormData();
    fd.append('filename', f.name);
    const resp = await axios.post('shipment/packing-lists/parse-filename', fd);
    filenamePreview.value = resp.data;
  } catch (e) {
    filenamePreview.value = { ok: false, message: e.response?.data?.message || 'Failed to parse filename' };
  }
};

const importLoading = ref(false);
const importProgress = ref(0);
const importProgressMessage = ref("");
const importSuccessMessage = ref("");
const importErrorMessage = ref("");
const importIntervalId = ref(null);
const importId = ref(null);
const selectedRows = ref([]);
const deleteDialog = ref(false);
const showConfirmDeleteDialog = ref(false);
const itemToDelete = ref(null);

// Format date helper function
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);

  return date.toLocaleDateString();
};

// Confirm delete function
const confirmDelete = (item) => {
  itemToDelete.value = item;
  deleteDialog.value = true;
};

const {
  packingLists,
  packingListsMeta,
  loading,
  fetchPackingLists,
  deletePackingList,
  deleteMultiplePackingLists,
} = usePackingListsManagement();

// Pagination variables
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalItems = ref(0);
const totalPages = ref(1);

// Same duplicate-fetch guard as the incoming-parcels page. Without this,
// VDataTableServer's initial @update:options emission AND onMounted both
// trigger applyFilters(), firing the same /shipment/packing-lists query
// twice back-to-back.
const pageReady = ref(false);

// Server-side sort state. Default to "id desc" so the latest PL (highest
// id) appears first on the warehouse team's main work list. The previous
// default came back from the backend in PK-ascending order, which made
// the column header's sort arrow disagree with the visible row order.
const sortBy = ref("id");
const sortOrder = ref("desc");

// Container filter (e.g. "9.2" or "9.2 10.9" to match several containers
// at once). Debounced so we don't fire a request on every keystroke.
const containerFilter = ref("");
let containerFilterTimeout = null;
const onContainerFilterChange = (value) => {
  if (containerFilterTimeout) clearTimeout(containerFilterTimeout);
  containerFilterTimeout = setTimeout(() => {
    currentPage.value = 1;
    applyFilters();
  }, 400);
};

// Two-way bound to VDataTableServer's sort-by — keeps the visible header
// arrow in sync with what we actually request from the API.
const tableSort = ref([{ key: "id", order: "desc" }]);

// Delete a packing list
const deleteItem = async () => {
  if (!itemToDelete.value) return;

  try {
    await deletePackingList(itemToDelete.value.id);
    success(
      t("messages.packing_list_deleted") || "Packing list deleted successfully"
    );
    deleteDialog.value = false;
    itemToDelete.value = null;
    applyFilters();
  } catch (err) {
    error(t("messages.deleteError") || "Failed to delete packing list");
  }
};

// Confirm delete selected rows
const confirmMassDelete = () => {
  if (!selectedRows.value.length) return;
  showConfirmDeleteDialog.value = true;
};

// Delete Selected Rows
const handleDeleteSelected = async () => {
  if (!selectedRows.value.length) return;

  try {
    await deleteMultiplePackingLists(selectedRows.value);

    showConfirmDeleteDialog.value = false;
    selectedRows.value = [];

    // Refresh the data to reflect deleted items
    await applyFilters();

    // Show success message
    success(
      t("messages.packingListsDeleted", "Packing lists deleted successfully")
    );
  } catch (err) {
    console.error("Error in handleDeleteSelected:", err);
    error(t("messages.deleteError", "Error deleting packing lists"));
  }
};

// Apply filters with pagination
const applyFilters = () => {
  const params = {
    page: currentPage.value,
    per_page: itemsPerPage.value,
    sort_by: sortBy.value,
    sort_order: sortOrder.value,
  };

  if (containerFilter.value?.trim()) {
    params.container = containerFilter.value.trim();
  }

  fetchPackingLists(params)
    .then((data) => {
      // Update pagination metadata
      totalItems.value = packingListsMeta.value?.total || 0;
      totalPages.value = packingListsMeta.value?.last_page || 1;
    })
    .catch((err) => {
      error(t("messages.fetchError", "Error fetching packing lists"));
    });
};

// Handle VDataTableServer options update
const handleTableOptionsUpdate = (options) => {
  // Skip the first emission on mount — onMounted drives the initial fetch.
  if (!pageReady.value) return;
  if (options.page !== currentPage.value) {
    currentPage.value = options.page;
  }
  if (options.itemsPerPage !== itemsPerPage.value) {
    itemsPerPage.value = options.itemsPerPage;
    currentPage.value = 1;
  }
  // Pick up sort changes from header clicks. Vuetify gives an array even
  // for single-column sort; we only support one column at a time on the
  // server so take the first entry.
  if (options.sortBy && options.sortBy.length > 0) {
    sortBy.value = options.sortBy[0].key;
    sortOrder.value = options.sortBy[0].order || "asc";
  } else {
    // Header click that clears the sort — fall back to default (id desc).
    sortBy.value = "id";
    sortOrder.value = "desc";
  }
  applyFilters();
};

// Function to handle page changes
const onPageChange = (page) => {
  currentPage.value = page;
  applyFilters();
};

// Function to handle items per page changes
const onItemsPerPageChange = (perPage) => {
  itemsPerPage.value = perPage;
  currentPage.value = 1; // Reset to first page when changing items per page
  applyFilters();
};

// Handle Excel file upload
const uploadExcelFile = async () => {
  if (!importForm.value.excel_file) {
    error("Please select an Excel file");
    return;
  }
  if (!filenamePreview.value?.ok) {
    error(filenamePreview.value?.message || "Filename must follow M.D_SEA (e.g. 1.23_SEA.xlsx)");
    return;
  }

  importLoading.value = true;
  importProgress.value = 5;
  importProgressMessage.value = "Uploading file...";
  importErrorMessage.value = "";
  importSuccessMessage.value = "";

  try {
    const formData = new FormData();

    // The backend parses the filename to derive reference_code +
    // container — so we no longer send container_arrival_id or
    // reference_code from the UI. Optional import_date is still useful.
    formData.append("excel_file", importForm.value.excel_file);
    if (importForm.value.import_date) {
      formData.append("import_date", importForm.value.import_date);
    }

    console.log("FormData content:");
    for (let pair of formData.entries()) {
      console.log(pair[0] + ": ", pair[1]);
    }

    // Upload the file
      const response = await axios.post(
        "shipment/packing-lists/import-existing",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

    if (response.data.success) {
      console.log("import response:", response.data);
      importId.value = response.data.import_id;
      importSuccessMessage.value = "Import started successfully";
      importProgress.value = 10;
      importProgressMessage.value = "Processing file...";

      // Start polling for progress updates
      startProgressPolling();
    } else {
      importErrorMessage.value = response.data.message || "Import failed";
      importLoading.value = false;
      error(response.data.message || "Import failed");
    }
  } catch (err) {
    console.error("Error uploading file:", err);
    importErrorMessage.value =
      err.response?.data?.message || "Failed to upload file";
    importLoading.value = false;
    error("Failed to upload file");
  }
};

// Poll for import progress updates
const startProgressPolling = () => {
  // Clear any existing interval
  if (importIntervalId.value) {
    clearInterval(importIntervalId.value);
  }

  // Start new polling interval
  importIntervalId.value = setInterval(async () => {
    try {
      const response = await axios.get(
        `shipment/packing-lists/import-progress/${importId.value}`
      );

      if (response.data.success) {
        const progressData = response.data.data;

        importProgress.value = progressData.progress || 0;
        importProgressMessage.value = progressData.message;

        // Check if import is complete
        if (
          progressData.status === "completed" ||
          progressData.progress >= 100
        ) {
          stopProgressPolling();

          if (progressData.status === "completed") {
            // Build a precise summary using the structured counts the backend
            // now sends. Falls back to the human message if counts aren't there
            // (e.g. legacy code path).
            const counts = progressData.counts || {};
            const parts = [];
            if (counts.imported !== undefined) parts.push(`${counts.imported} imported`);
            if (counts.created_new) parts.push(`${counts.created_new} new`);
            if (counts.duplicates) parts.push(`${counts.duplicates} duplicates`);

            importSuccessMessage.value = parts.length
              ? `Import done — ${parts.join(' · ')}`
              : (progressData.message || "Import completed successfully");
            success(importSuccessMessage.value);

            // If the import parked any rows, surface them with a follow-up
            // warning toast and open the inbox in a new tab so the tester
            // doesn't lose their place on the PL list. (useSnackbar doesn't
            // support clickable toasts, hence the auto-open.)
            if (counts.parked > 0) {
              warning(`${counts.parked} row${counts.parked > 1 ? 's' : ''} need review — opening Pending Imports.`);
              setTimeout(() => {
                window.open(router.resolve({ name: 'shipment-pending-imports' }).href, '_blank');
              }, 800);
            }

            fetchPackingLists();
            setTimeout(() => {
              importDialog.value = false;
              resetImportForm();
            }, 2000);
          } else {
            importErrorMessage.value = progressData.message || "Import failed";
            error(progressData.message || "Import failed");
          }

          importLoading.value = false;
        }
      } else {
        stopProgressPolling();
        importErrorMessage.value = "Failed to fetch import progress";
        importLoading.value = false;
      }
    } catch (err) {
      console.error("Error checking import progress:", err);
    }
  }, 2000); // Poll every 2 seconds
};

// Stop polling for progress updates
const stopProgressPolling = () => {
  if (importIntervalId.value) {
    clearInterval(importIntervalId.value);
    importIntervalId.value = null;
  }
};

// Reset import form and state
const resetImportForm = () => {
  importForm.value = {
    excel_file: null,
    import_date: null,
  };
  filenamePreview.value = null;
  importProgress.value = 0;
  importProgressMessage.value = "";
  importSuccessMessage.value = "";
  importErrorMessage.value = "";
  importId.value = null;
  importLoading.value = false;
};

// Watch for dialog close to reset form
watch(importDialog, (newVal) => {
  if (!newVal) {
    // Reset form and stop polling when dialog closes
    stopProgressPolling();
    resetImportForm();
  }
});

// Lifecycle hooks
onMounted(async () => {
  await applyFilters();
  await nextTick();
  pageReady.value = true;
});

// Computed Headers
const headers = computed(() => [
  { title: "ID", key: "id", align: "start" },
  { title: "Date", key: "created_at", align: "start" },
  { title: "Container", key: "container_arrival", align: "start" },
  { title: "Package Count", key: "total_package", align: "center" },
  { title: "Weight", key: "weight", align: "end" },
  { title: "Volume", key: "volume", align: "end" },
  { title: "Status", key: "status", align: "center" },
  { title: "Actions", key: "actions", align: "center", sortable: false },
]);
</script>

<style scoped>
.filter-select {
  min-width: 200px;
}
</style>
